<template>
  <div
    class="w-full flex-1 flex flex-col items-center justify-between p-3 sm:p-4 bg-gray-950 text-white min-h-[460px] sm:min-h-[520px] rounded-2xl relative overflow-hidden shadow-md">
    <!-- Camera Preview Screen -->
    <div v-if="!capturedImage"
      class="w-full flex-1 flex flex-col items-center justify-center relative min-h-[360px] sm:min-h-[420px]">
      <video ref="videoRef" autoplay playsinline muted
        class="w-full h-full min-h-[360px] sm:min-h-[420px] object-cover rounded-xl bg-black"></video>

      <!-- Watermark overlay on live viewfinder -->
      <div v-if="showWatermark"
        class="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur-xs p-2.5 rounded-xl text-[11px] text-gray-200 pointer-events-none z-10 border border-white/10">
        <div class="font-bold text-white uppercase tracking-wide">{{ watermarkTitle }}</div>
        <div class="font-mono text-gray-300 text-[10px] mt-0.5">{{ currentFormattedTime }}</div>
      </div>

      <!-- Capture Button Trigger (Placed prominently over bottom of viewfinder) -->
      <div class="absolute bottom-16 left-0 right-0 flex items-center justify-center gap-6 z-20">
        <v-btn fab x-large color="white" class="shadow-2xl hover:scale-105 active:scale-95 transition-transform"
          @click="capturePhoto">
          <v-icon color="indigo darken-3" large>mdi-camera</v-icon>
        </v-btn>

        <v-btn v-if="hasMultipleCameras" icon large color="white" class="bg-black/50 backdrop-blur-md shadow-lg"
          @click="toggleCamera">
          <v-icon>mdi-camera-flip-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Captured Photo Preview & Confirmation -->
    <div v-else class="w-full flex-1 flex flex-col items-center justify-center relative min-h-[360px] sm:min-h-[420px]">
      <img :src="capturedImage" alt="Captured Selfie"
        class="w-full h-full min-h-[360px] sm:min-h-[420px] object-cover rounded-xl shadow-lg border border-gray-800" />

      <!-- Action Footer -->
      <div class="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-4 z-20 px-4">
        <v-btn depressed color="grey darken-3" class="white--text rounded-xl h-10 px-5 text-xs font-bold shadow-md"
          :disabled="uploading" @click="retakePhoto">
          <v-icon left small>mdi-refresh</v-icon>
          Retake
        </v-btn>

        <v-btn depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl h-10 px-6 text-xs font-extrabold shadow-lg"
          :loading="uploading" @click="confirmAndUpload">
          <v-icon left small>mdi-check</v-icon>
          Submit
        </v-btn>
      </div>
    </div>

    <!-- Hidden Canvas used to render capture + stamped watermark -->
    <canvas ref="canvasRef" class="hidden"></canvas>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'AppSelfieCamera',
  props: {
    uploading: {
      type: Boolean,
      default: false
    },
    watermarkText: {
      type: String,
      default: 'FINFI VISIT VERIFICATION'
    },
    taskData: {
      type: Object,
      default: null
    },
    showWatermark: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      stream: null,
      capturedImage: null,
      facingMode: 'user',
      hasMultipleCameras: false,
      currentTime: new Date()
    }
  },
  computed: {
    watermarkTitle() {
      if (this.taskData?.caseNo) {
        return `CASE #${this.taskData.caseNo} - ${this.watermarkText}`
      }
      return this.watermarkText
    },
    currentFormattedTime() {
      return dayjs(this.currentTime).format('DD/MM/YYYY hh:mm:ss A')
    }
  },
  mounted() {
    this.initCamera()
    this.checkCameras()
    this.timer = setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
  },
  beforeDestroy() {
    this.stopCamera()
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async checkCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter((d) => d.kind === 'videoinput')
        this.hasMultipleCameras = videoDevices.length > 1
      } catch (err) {
        console.warn('Could not enumerate video devices:', err)
      }
    },
    async initCamera() {
      try {
        this.stopCamera()
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: this.facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
        })
        if (this.$refs.videoRef) {
          this.$refs.videoRef.srcObject = this.stream
        }
      } catch (err) {
        console.error('Camera access error:', err)
        this.$toast?.error?.('Unable to access device camera. Please grant camera permission.')
      }
    },
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop())
        this.stream = null
      }
    },
    toggleCamera() {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user'
      this.initCamera()
    },
    capturePhoto() {
      const video = this.$refs.videoRef
      const canvas = this.$refs.canvasRef
      if (!video || !canvas) return

      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 480
      const ctx = canvas.getContext('2d')

      // Draw mirrored video if user camera
      if (this.facingMode === 'user') {
        ctx.translate(canvas.width, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        ctx.setTransform(1, 0, 0, 1, 0, 0)
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      }

      // Stamp GPS & Time Watermark on canvas
      if (this.showWatermark) {
        const lines = [
          this.watermarkTitle,
          `Captured: ${this.currentFormattedTime}`,
          this.taskData?.customer?.customerFullName ? `Customer: ${this.taskData.customer.customerFullName}` : ''
        ].filter(Boolean)

        const padding = 12
        const lineHeight = 18
        const boxHeight = lines.length * lineHeight + padding * 2
        const boxY = canvas.height - boxHeight - 10

        ctx.fillStyle = 'rgba(0, 0, 0, 0.65)'
        ctx.fillRect(10, boxY, canvas.width - 20, boxHeight)

        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 14px sans-serif'
        lines.forEach((line, idx) => {
          ctx.fillText(line, 20, boxY + padding + lineHeight * (idx + 0.8))
        })
      }

      this.capturedImage = canvas.toDataURL('image/jpeg', 0.85)
    },
    retakePhoto() {
      this.capturedImage = null
      this.$nextTick(() => {
        if (this.$refs.videoRef && this.stream) {
          this.$refs.videoRef.srcObject = this.stream
        }
      })
    },
    confirmAndUpload() {
      if (!this.capturedImage) return
      // Convert base64 DataURL to Blob File
      const byteString = atob(this.capturedImage.split(',')[1])
      const mimeString = this.capturedImage.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: mimeString })
      const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: mimeString })

      this.$emit('captured', { file, dataUrl: this.capturedImage })
    }
  }
}
</script>
